import React, { useState, useRef, useEffect } from 'react';

interface Option {
  value: string;
  label: string;
}

interface SearchableDropdownProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  valueLabel?: string; // optional explicit label when options not yet loaded
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  tabIndex?: number;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  enableAutoComplete?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = "Сонгох",
  valueLabel,
  disabled = false,
  className = "",
  tabIndex,
  onKeyDown,
  enableAutoComplete = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [autoCompleteText, setAutoCompleteText] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownListRef = useRef<HTMLDivElement>(null);

  // Sort options by Mongolian Cyrillic alphabet.
  // Keep the "All" option (value === '') always at the top.
  const allOptions = options.filter(option => option && option.label);
  const pinnedTopOptions = allOptions.filter(option => option.value === '');
  const sortableOptions = allOptions
    .filter(option => option.value !== '')
    .sort((a, b) => a.label.localeCompare(b.label, 'mn-MN'));
  const sortedOptions = [...pinnedTopOptions, ...sortableOptions];

  // Filter options based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredOptions(sortedOptions);
      setSelectedIndex(-1);
      setAutoCompleteText("");
    } else {
      const filtered = sortedOptions.filter(option =>
        option && option.label && option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
      
      // Auto-completion logic
      if (enableAutoComplete && filtered.length > 0) {
        const firstMatch = filtered[0];
        if (firstMatch && firstMatch.label && firstMatch.label.toLowerCase().startsWith(searchTerm.toLowerCase())) {
          const remainingText = firstMatch.label.substring(searchTerm.length);
          setAutoCompleteText(remainingText);
          setSelectedIndex(0);
        } else {
          setAutoCompleteText("");
          setSelectedIndex(-1);
        }
      } else {
        setAutoCompleteText("");
        setSelectedIndex(-1);
      }
    }
  }, [searchTerm, options, enableAutoComplete]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option: Option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm("");
    setAutoCompleteText("");
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (enableAutoComplete && isOpen) {
      if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault();
        if (selectedIndex >= 0 && filteredOptions[selectedIndex]) {
          handleSelect(filteredOptions[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm("");
        setAutoCompleteText("");
        setSelectedIndex(-1);
      }
    }
    
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const scrollToSelected = (index: number) => {
    if (dropdownListRef.current && index >= 0) {
      const options = dropdownListRef.current.children;
      if (options[index]) {
        const selectedElement = options[index] as HTMLElement;
        selectedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm("");
      }
    }
  };

  const selectedOption = options.find(option => option.value === value);
  const resolvedLabel = selectedOption?.label || valueLabel;

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          placeholder={placeholder}
          value={isOpen ? searchTerm : (resolvedLabel || '')}
          onChange={(e) => {
            if (isOpen) {
              setSearchTerm(e.target.value);
            }
          }}
                                onKeyDown={(e) => {
             if (e.key === 'Enter') {
               e.preventDefault();
               if (isOpen && selectedIndex >= 0 && filteredOptions[selectedIndex]) {
                 handleSelect(filteredOptions[selectedIndex]);
               } else if (!isOpen) {
                 handleToggle();
               }
             } else if (e.key === 'Tab') {
               // Only prevent default if dropdown is open and we have a selection
               if (isOpen && selectedIndex >= 0 && filteredOptions[selectedIndex]) {
                 e.preventDefault();
                 handleSelect(filteredOptions[selectedIndex]);
               }
               // If dropdown is closed or no selection, let Tab work normally for field navigation
             } else if (e.key === 'Escape') {
               setIsOpen(false);
               setSearchTerm("");
             } else if (e.key === 'ArrowDown') {
               e.preventDefault();
               if (!isOpen) {
                 handleToggle();
               } else if (selectedIndex < filteredOptions.length - 1) {
                 const newIndex = selectedIndex + 1;
                 setSelectedIndex(newIndex);
                 scrollToSelected(newIndex);
               }
             } else if (e.key === 'ArrowUp') {
               e.preventDefault();
               if (isOpen && selectedIndex > 0) {
                 const newIndex = selectedIndex - 1;
                 setSelectedIndex(newIndex);
                 scrollToSelected(newIndex);
               }
             } else if (!isOpen) {
               handleToggle();
               setSearchTerm((e.target as HTMLInputElement).value);
             }
           }}
          onFocus={() => {
            if (!isOpen) {
              handleToggle();
            }
          }}
          className={`w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
          }`}
          tabIndex={tabIndex}
          disabled={disabled}
        />
        {isOpen && enableAutoComplete && autoCompleteText && (
          <div className="absolute top-0 left-0 pointer-events-none z-0 w-full h-full flex items-center">
            <span className="text-sm text-transparent px-2 py-1">
              {searchTerm}
              <span className="text-blue-600 font-medium">{autoCompleteText}</span>
            </span>
          </div>
        )}
        <span className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
          {isOpen ? '▲' : '▼'}
        </span>
      </div>

                    {isOpen && (
         <div ref={dropdownListRef} className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
           {filteredOptions.length > 0 ? (
             filteredOptions.map((option, index) => (
               <div
                 key={option.value}
                 className={`px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm ${
                   option.value === value ? 'bg-blue-100' : ''
                 } ${index === selectedIndex ? 'bg-blue-200' : ''}`}
                 onClick={() => handleSelect(option)}
               >
                 {option.label}
               </div>
             ))
           ) : (
             <div className="px-3 py-2 text-sm text-gray-500">
               Олдсонгүй
             </div>
           )}
         </div>
       )}
    </div>
  );
};

export default SearchableDropdown; 